import { useState } from "react";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { ALL_UNITS, BINARY_UNITS, DECIMAL_UNITS, advertisedToShown, formatNumber, fromBytes, sizeError, toBytes } from "./sizeUnits";

const TOOL_INFO_CONTENT = {
  what: `Converts a file or storage size between bytes, KB, MB, GB, TB and PB in both systems: decimal (1 GB = 1,000,000,000 bytes, used by drive makers, phone makers and networks) and binary (1 GiB = 1,073,741,824 bytes, used by Windows, many file tools and memory).`,
  how: `Enter a value and pick its unit. Both columns update at once: the decimal units (KB, MB, GB...) and the binary units (KiB, MiB, GiB...). The box below shows what a drive sold in decimal GB or TB shows up as on a computer that counts in 1024s.`,
  tip: `That is why a 500 GB drive shows about 465 GB in Windows: the box counts 500,000,000,000 bytes, and Windows divides by 1024³ but still writes "GB". macOS divides by 1000 and shows the full 500 GB.`,
};

export default function FileSizeConverter() {
  const [value, setValue] = useState("1");
  const [from, setFrom] = useState("GB");
  const [drive, setDrive] = useState("500");
  const [driveUnit, setDriveUnit] = useState<"GB" | "TB">("GB");

  const num = Number(value);
  const err = sizeError(value);
  const bytes = value.trim() === "" || isNaN(num) || err ? NaN : toBytes(num, from);
  const dv = Number(drive);
  const shown = drive.trim() !== "" && dv > 0 ? advertisedToShown(dv, driveUnit) : null;

  const { blocked } = useLogToolUsage(Number.isFinite(bytes) ? bytes : null);
  if (blocked) return <ToolContainer><p className="text-sm text-muted-foreground text-center py-10">You have used today's trial uses. A day pass or plan removes the limit.</p></ToolContainer>;

  return (
    <ToolContainer>
      <div className="max-w-xl mx-auto space-y-5">
        <div className="flex gap-3">
          <div className="flex-1"><Label htmlFor="fs-value">Value</Label><Input id="fs-value" type="number" min={0} value={value} aria-invalid={!!err} onChange={e => setValue(e.target.value)} />{err && <p role="alert" className="text-xs text-destructive mt-1">{err}</p>}</div>
          <div className="w-36"><Label>Unit</Label>
            <Select value={from} onValueChange={setFrom}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{ALL_UNITS.map(u => <SelectItem key={u.label} value={u.label}>{u.label}{u.binary ? " (1024)" : u.label === "B" ? "" : " (1000)"}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[{ title: "Decimal (1000)", units: DECIMAL_UNITS }, { title: "Binary (1024)", units: BINARY_UNITS }].map(col => (
            <div key={col.title} className="space-y-2 p-4 rounded-lg border bg-card">
              <p className="text-xs font-medium text-muted-foreground">{col.title}</p>
              {col.units.map(u => (
                <div key={u.label} className="flex justify-between text-sm gap-2">
                  <span className="text-muted-foreground">{u.label}</span>
                  <span className="font-mono font-medium text-right">{formatNumber(fromBytes(bytes, u))}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="p-4 rounded-lg border bg-card space-y-3">
          <p className="text-sm font-medium">Why does my drive show less than the box says?</p>
          <div className="flex gap-3 items-end">
            <div className="flex-1"><Label htmlFor="fs-drive" className="text-xs">Advertised size</Label><Input id="fs-drive" type="number" min={0} value={drive} onChange={e => setDrive(e.target.value)} /></div>
            <Select value={driveUnit} onValueChange={v => setDriveUnit(v as "GB" | "TB")}>
              <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="GB">GB</SelectItem><SelectItem value="TB">TB</SelectItem></SelectContent>
            </Select>
          </div>
          {shown && (
            <p className="text-sm text-muted-foreground">
              {formatNumber(dv)} {driveUnit} on the box is <strong className="text-foreground">{formatNumber(driveUnit === "GB" ? shown.gib : shown.tib)} {driveUnit === "GB" ? "GiB" : "TiB"}</strong>.
              Windows labels that "{driveUnit}", so you see about {formatNumber(Math.round((driveUnit === "GB" ? shown.gib : shown.tib) * 100) / 100)} {driveUnit} — {shown.lostPct.toFixed(1)}% "missing", though no bytes are lost. macOS shows the full {formatNumber(dv)} {driveUnit}.
            </p>
          )}
        </div>
      </div>
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
